import { type ReactNode } from 'react';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import appStoreBadge from '@/assets/images/app-store.svg';
import playStoreBadge from '@/assets/images/playstore.svg';
import { APP_STORE_URL, PLAY_STORE_URL } from '@/constants';
import type { Platform } from '@/utils/helpers/platform.helpers';
import { track } from '@/analytics';

type StoreSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platform: Platform;
  childFirstName?: string;
};

const COPY: Record<Platform, { title: string; primary?: 'ios' | 'android' }> = {
  ios: { title: 'Opening the App Store', primary: 'ios' },
  android: { title: 'Opening Google Play', primary: 'android' },
  // AC 3 — no primary; both badges carry equal weight.
  other: { title: 'Get the Mia app' },
};

const STORES = {
  ios: { label: 'Download on the App Store', href: APP_STORE_URL, badge: appStoreBadge },
  android: { label: 'Get it on Google Play', href: PLAY_STORE_URL, badge: playStoreBadge },
} as const;

/**
 * Routes someone to the store that matches their device (GIFTWEB-27).
 *
 * Every link is a plain `https://` store URL. Those are universal links: iOS
 * and Android open the store app itself (AC 6), while a desktop or an
 * unrecognised browser still lands somewhere real. `itms-apps://` and
 * `market://` would open the app too, but dead-end everywhere else.
 */
function StoreSheet({
  open,
  onOpenChange,
  platform,
  childFirstName,
}: StoreSheetProps) {
  const { title, primary } = COPY[platform];
  const child = childFirstName?.trim();
  const other = primary === 'ios' ? 'android' : 'ios';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='text-center'>
        <SheetHeader className='w-full text-center'>
          <SheetTitle className='text-[20px] font-bold tracking-[-0.01em] text-[#1a201d]'>
            {title}
          </SheetTitle>
        </SheetHeader>

        <SheetDescription asChild>
          <p className='mt-[7px] text-[13px] leading-[1.5] text-[#5a635e]'>
            Sign in with the email you used today, so {child ? `${child}'s` : 'their'}{' '}
            gift is already waiting. Mia is for children aged 16 and under.
          </p>
        </SheetDescription>

        {primary ? (
          <>
            <StoreLink store={primary} variant='primary' />
            <a
              href={STORES[other].href}
              target='_blank'
              rel='noreferrer'
              onClick={() => track('appStoreOpened', { store: other })}
              className='mx-auto mt-2.5 block text-[12.5px] font-semibold text-[#5a635e] underline underline-offset-2'
            >
              Not on {primary === 'ios' ? 'iPhone' : 'Android'}?{' '}
              {STORES[other].label}
            </a>
          </>
        ) : (
          <div className='mt-[18px] flex flex-wrap items-center justify-center gap-3'>
            <StoreLink store='ios' variant='badge' />
            <StoreLink store='android' variant='badge' />
          </div>
        )}

        <button
          type='button'
          onClick={() => onOpenChange(false)}
          className='mx-auto mt-4 block text-[13.5px] font-semibold text-[#8a8997]'
        >
          Not now
        </button>
      </SheetContent>
    </Sheet>
  );
}

function StoreLink({
  store,
  variant,
}: {
  store: 'ios' | 'android';
  variant: 'primary' | 'badge';
}): ReactNode {
  const { label, href, badge } = STORES[store];

  if (variant === 'badge') {
    return (
      <a
        href={href}
        target='_blank'
        rel='noreferrer'
        aria-label={label}
        onClick={() => track('appStoreOpened', { store })}
      >
        <img src={badge} alt={label} className='h-11 w-auto' />
      </a>
    );
  }

  return (
    <a
      href={href}
      target='_blank'
      rel='noreferrer'
      data-testid='store-primary'
      onClick={() => track('appStoreOpened', { store })}
      className='mt-[18px] flex h-[54px] w-full items-center justify-center gap-2.5 rounded-[13px] bg-[#0d0d0f] text-[15px] font-semibold text-white'
    >
      {store === 'ios' ? (
        <svg width='18' height='18' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
          <path d='M16.4 12.7c0-2.4 2-3.5 2-3.6-1.1-1.6-2.8-1.8-3.4-1.8-1.5-.2-2.8.8-3.6.8-.7 0-1.8-.8-3-.8-1.6 0-3 .9-3.8 2.3-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.4 2.9 2.3 1.2 0 1.6-.7 3-.7s1.8.7 3 .7c1.3 0 2.1-1.1 2.8-2.3.9-1.3 1.3-2.6 1.3-2.7 0 0-2.4-.9-2.4-3.5zM14 5.9c.6-.8 1.1-1.9 1-3-1 0-2.1.6-2.8 1.5-.6.7-1.1 1.9-1 3 1.1.1 2.2-.6 2.8-1.5z' />
        </svg>
      ) : (
        <svg width='18' height='18' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
          <path d='M3.6 2.2a1 1 0 0 0-.5.9v17.8a1 1 0 0 0 .5.9l9.4-9.8-9.4-9.8zM14.4 13l2.6 2.7-9.7 5.5 7.1-8.2zm0-2L7.3 2.8l9.7 5.5L14.4 11zm3.9 1.4 2.6-1.5a1 1 0 0 0 0-1.8l-2.6-1.5L15.6 12l2.7 2.4z' />
        </svg>
      )}
      {label}
    </a>
  );
}

export default StoreSheet;
